import { createSelector } from 'reselect';
import _omit from 'lodash/omit';
import { get } from 'ravenjs/utils/lodash';

import * as authActions from 'modules/auth/actions';
import * as casePresenceActions from 'modules/casePresence/actions';
import { createReducer } from 'utils/reducer';

/**
 * Initial state for case presence.
 * Shape: { viewers: { [caseId]: [...viewerObjects] }, subscriptions: { [caseId]: true } }
 *
 * Each viewer object: { user_id, display_name, viewing_since, last_active_at }
 *
 * @type {Object}
 */
const INITIAL_STATE = {
    viewers: {},
    subscriptions: {},
};

/**
 * Handle viewer updates from the TicketActivityChannel broadcast.
 *
 * @method handleUpdateCaseViewers
 * @param  {Object} state  The current state
 * @param  {Object} action The dispatched action
 * @return {Object}        The updated state
 */
export const handleUpdateCaseViewers = (state, action) => {
    const caseId = get(action, 'payload.caseId');
    const viewers = get(action, 'payload.viewers', []);

    if (!caseId) return state;

    return {
        ...state,
        viewers: {
            ...state.viewers,
            [caseId]: viewers,
        },
    };
};

/**
 * Track that a subscription is active for a case.
 *
 * @method handleSubscribeSuccess
 * @param  {Object} state  The current state
 * @param  {Object} action The dispatched action
 * @return {Object}        The updated state
 */
export const handleSubscribeSuccess = (state, action) => {
    const caseId = get(action, 'payload.caseId');

    if (!caseId) return state;

    return {
        ...state,
        subscriptions: {
            ...state.subscriptions,
            [caseId]: true,
        },
    };
};

/**
 * Remove subscription tracking for a case.
 * Used by both unsubscribe success and subscribe error handlers.
 *
 * @method handleRemoveSubscription
 * @param  {Object} state  The current state
 * @param  {Object} action The dispatched action
 * @return {Object}        The updated state
 */
export const handleRemoveSubscription = (state, action) => {
    const caseId = get(action, 'payload.caseId');

    if (!caseId) return state;

    return {
        ...state,
        subscriptions: _omit(state.subscriptions, [caseId]),
    };
};

/**
 * Clear all presence data for a specific case.
 *
 * @method handleClearCasePresence
 * @param  {Object} state  The current state
 * @param  {Object} action The dispatched action
 * @return {Object}        The updated state
 */
export const handleClearCasePresence = (state, action) => {
    const caseId = get(action, 'payload.caseId');

    if (!caseId) return state;

    return {
        ...state,
        viewers: _omit(state.viewers, [caseId]),
        subscriptions: _omit(state.subscriptions, [caseId]),
    };
};

/**
 * Reset state on logout.
 *
 * @method handleLogout
 * @return {Object} The initial state
 */
export const handleLogout = () => INITIAL_STATE;

/**
 * The casePresence reducer.
 */
const reducer = createReducer(INITIAL_STATE, {
    [casePresenceActions.updateCaseViewers.SUCCESS]: handleUpdateCaseViewers,
    [casePresenceActions.subscribeCasePresence.SUCCESS]: handleSubscribeSuccess,
    [casePresenceActions.subscribeCasePresence.ERROR]: handleRemoveSubscription,
    [casePresenceActions.unsubscribeCasePresence.SUCCESS]: handleRemoveSubscription,
    [casePresenceActions.clearCasePresence.SUCCESS]: handleClearCasePresence,
    [authActions.logout.TRIGGER]: handleLogout,
});

export default reducer;

// --- Selectors ---

/**
 * Get the casePresence slice of state.
 *
 * @method getCasePresence
 * @param  {Object} state The Redux state
 * @return {Object}       The casePresence slice
 */
export const getCasePresence = state => get(state, 'casePresence', {});

/**
 * Get the viewers map (all cases).
 */
export const getAllViewers = createSelector([getCasePresence], casePresence =>
    get(casePresence, 'viewers', {})
);

/**
 * Get the subscriptions map.
 */
export const getSubscriptions = createSelector([getCasePresence], casePresence =>
    get(casePresence, 'subscriptions', {})
);

/**
 * Get viewers for a specific case by ID.
 *
 * @param {Object} state  The Redux state
 * @param {string} caseId The case ID
 * @return {Array}        The list of viewer objects
 */
export const getViewersForCase = (state, caseId) =>
    get(state, `casePresence.viewers.${caseId}`, []);

/**
 * Get the viewer count for a specific case.
 *
 * @param {Object} state  The Redux state
 * @param {string} caseId The case ID
 * @return {number}       The number of viewers
 */
export const getViewerCountForCase = (state, caseId) => getViewersForCase(state, caseId).length;

/**
 * Check if a specific user is viewing a case.
 *
 * @param {Object}        state  The Redux state
 * @param {string}        caseId The case ID
 * @param {string|number} userId The user ID to check
 * @return {boolean}
 */
export const isUserViewingCase = (state, caseId, userId) =>
    getViewersForCase(state, caseId).some(viewer => String(viewer.user_id) === String(userId));

/**
 * Check if a subscription is active for a case.
 *
 * @param {Object} state  The Redux state
 * @param {string} caseId The case ID
 * @return {boolean}
 */
export const isSubscribedToCase = (state, caseId) =>
    get(state, `casePresence.subscriptions.${caseId}`, false);
